import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { Link, useLocation } from 'react-router-dom'
import CalendlyButton from './CalendlyButton'
import { bandTone, TONE_CLASSES, type SectionTone } from '../lib/section-tones'

/**
 * One of the two buttons in a CTA section.
 *
 * `asAnchor` preserves the plain `<a>` that four pages used before this component
 * existed. A plain anchor triggers a full page reload instead of a client-side
 * route change, pre-existing behaviour, kept deliberately so that a colour fix
 * changes nothing else. Worth unifying to `<Link>` under its own ticket.
 */
export type CtaAction =
  | { kind: 'calendly'; label: string }
  | { kind: 'link'; label: string; href: string; asAnchor?: boolean }

interface CtaSectionProps {
  heading: string
  description: ReactNode
  primary: CtaAction
  secondary: CtaAction
  footnote?: string
  /** `lg` starts the heading and body one step larger on mobile. */
  size?: 'default' | 'lg'
  /**
   * Override the band's tone. Normally omit it: the tone comes from the shared
   * table in `lib/section-tones`, which also drives the footer so the two can
   * never drift out of step.
   */
  tone?: SectionTone
}

const PRIMARY_CLASSES =
  'inline-flex items-center justify-center px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 w-full sm:w-auto'

// Light mode reads dark-on-light. The `dark:` half restores the original
// white-on-dark treatment exactly, so dark mode is unchanged.
const SECONDARY_CLASSES =
  'inline-flex items-center justify-center px-8 py-4 border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-gray-900 font-semibold rounded-lg transition-all duration-200 w-full sm:w-auto'

const HEADING_SIZES: Record<'default' | 'lg', string> = {
  default: 'text-3xl sm:text-4xl md:text-5xl',
  lg: 'text-4xl md:text-5xl',
}

const DESCRIPTION_SIZES: Record<'default' | 'lg', string> = {
  default: 'text-lg sm:text-xl',
  lg: 'text-xl',
}

const CtaActionButton = ({ action, className }: { action: CtaAction; className: string }) => {
  if (action.kind === 'calendly') {
    return <CalendlyButton className={className}>{action.label}</CalendlyButton>
  }

  if (action.asAnchor) {
    return (
      <a href={action.href} className={className}>
        {action.label}
      </a>
    )
  }

  return (
    <Link to={action.href} className={className}>
      {action.label}
    </Link>
  )
}

/**
 * The closing call-to-action band shared by the service, location and blog pages.
 *
 * Every colour here declares its light value in the base class and its dark value
 * behind `dark:`. Writing the dark value bare, `bg-gray-900` with no light
 * counterpart, is what left 24 blocks stuck on navy in light mode (DMX-025).
 *
 * The band's colour comes from `lib/section-tones`, which holds only the site's
 * two canonical section pairs and also drives the footer's tone, so the band and
 * the footer always contrast with each other.
 */
const CtaSection = ({
  heading,
  description,
  primary,
  secondary,
  footnote,
  size = 'default',
  tone,
}: CtaSectionProps) => {
  const { pathname } = useLocation()
  const resolved = tone ?? bandTone(pathname)

  return (
  <section
    className={`section-padding ${TONE_CLASSES[resolved]} transition-colors duration-200`}
  >
    <div className="container-custom">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h2
          className={`${HEADING_SIZES[size]} font-bold text-gray-900 dark:text-white mb-6`}
        >
          {heading}
        </h2>
        <p
          className={`${DESCRIPTION_SIZES[size]} text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto`}
        >
          {description}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <CtaActionButton action={primary} className={PRIMARY_CLASSES} />
          <CtaActionButton action={secondary} className={SECONDARY_CLASSES} />
        </div>
        {footnote && (
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-6">{footnote}</p>
        )}
      </motion.div>
    </div>
  </section>
  )
}

export default CtaSection
